import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import api from '../api/axios';
import TableSearchInput from '../components/TableSearchInput';
import { textHaystackIncludes } from '../utils/textSearch';
const MILESTONE_DISPLAY_ORDER = [10, 50, 35, 5];

const MILESTONE_TITLES = {
  10: 'Payment reminder - 10% (Design Initiation)',
  50: 'Payment reminder - 50% (Execution Commencement)',
  35: 'Payment reminder - 35% (Basic Structure Completion)',
  5: 'Payment reminder - 5% (Inspection & Handover)',
};

function milestoneSortKey(pct) {
  const p = Number(pct);
  const i = MILESTONE_DISPLAY_ORDER.indexOf(p);
  return i === -1 ? 999 : i;
}

function splitTotalIntoMilestones(totalRupees) {
  const totalPaise = Math.round(Math.max(0, Number(totalRupees) || 0) * 100);
  if (totalPaise <= 0) return MILESTONE_DISPLAY_ORDER.map(() => 0);
  const out = [];
  let allocatedPaise = 0;
  for (let i = 0; i < MILESTONE_DISPLAY_ORDER.length; i++) {
    const pct = MILESTONE_DISPLAY_ORDER[i];
    const isLast = i === MILESTONE_DISPLAY_ORDER.length - 1;
    const sharePaise = isLast ? totalPaise - allocatedPaise : Math.floor((totalPaise * pct) / 100);
    out.push(Math.round(sharePaise) / 100);
    allocatedPaise += sharePaise;
  }
  return out;
}

function calculateMilestoneRows(rows, receiptsByPct) {
  const sortedRows = (rows || [])
    .slice()
    .sort((a, b) => milestoneSortKey(a.milestonePct) - milestoneSortKey(b.milestonePct));
  let carryForward = 0;
  return sortedRows.map((r, rowIndex) => {
    const pctKey = String(r.milestonePct);
    const receipt = receiptsByPct[pctKey];
    const received = Number(receipt?.amount) || 0;
    const milestoneAmount = Number(r.amount) || 0;
    const carryIn = carryForward;
    const netPayable = Math.round((milestoneAmount + carryIn) * 100) / 100;
    const pending = Math.max(0, Math.round((netPayable - received) * 100) / 100);
    const forwardsToNext = rowIndex < sortedRows.length - 1 && pending > 0 && received > 0;
    const displayedPending = forwardsToNext ? 0 : pending;
    carryForward = pending;
    return { ...r, pctKey, received, carryIn, milestoneAmount, netPayable, pending, forwardsToNext, displayedPending };
  });
}

const fmt = (n) => `₹ ${Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric'}) : '—';

// ── Tooltip ───────────────────────────────────────────────────────────────────
function Tooltip({ withoutGst, gst, total }) {
  return (
    <div style={{ position:'absolute',top:24,left:0,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',zIndex:100,minWidth:200,padding:'10px 14px',whiteSpace:'nowrap' }}>
      {[['Without GST', withoutGst],['GST', gst],['Total', total]].map(([l,v])=>(
        <div key={l} style={{ display:'flex',justifyContent:'space-between',gap:16,fontSize:12,color:'#374151',marginBottom:4 }}>
          <span>{l} :</span><span style={{ fontWeight:600 }}>{fmt(v)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Summary Stat ──────────────────────────────────────────────────────────────
function SummaryStat({ label, value, color='#10B981', icon, showInfo, withoutGst, gst, total }) {
  const [showTip, setShowTip] = useState(false);
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#6B7280',marginBottom:2 }}>
        {icon && <span style={{ fontSize:12 }}>{icon}</span>}
        <span style={{ color }}>{label}</span>
        {showInfo && (
          <div style={{ position:'relative' }}>
            <button onMouseEnter={()=>setShowTip(true)} onMouseLeave={()=>setShowTip(false)}
              style={{ width:14,height:14,borderRadius:'50%',border:'1px solid #9CA3AF',background:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#9CA3AF' }}>i</button>
            {showTip && <Tooltip withoutGst={withoutGst||0} gst={gst||0} total={total||value||0} />}
          </div>
        )}
      </div>
      <div style={{ fontSize:16,fontWeight:700,color:'#1F2937' }}>{fmt(value)}</div>
    </div>
  );
}

// ── Add Expense Modal ─────────────────────────────────────────────────────────
function AddExpenseModal({ clientId, projectId, onClose, onSaved }) {
  const [form, setForm] = useState({ amount:'', expenseType:'Reimbursement', title:'', date:new Date().toISOString().split('T')[0], description:'', ...(projectId ? { project: projectId } : {}) });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const iS = { width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',background:'#F9FAFB',fontFamily:'inherit' };
  const lS = { display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/clients/${clientId}/financials/expenses`, form);
      onSaved(); onClose();
    } catch(err) { alert(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}>
      <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:500,boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid #E5E7EB' }}>
          <h3 style={{ margin:0,fontSize:16,fontWeight:700 }}>Add Payment Expense</h3>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>
        <form onSubmit={submit} style={{ padding:'16px 20px' }}>
          <div style={{ marginBottom:12 }}>
            <label style={lS}>Amount *</label>
            <div style={{ position:'relative' }}>
              <span style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'#9CA3AF' }}>₹</span>
              <input style={{...iS,paddingLeft:24}} type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} required placeholder="Enter amount" />
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={lS}>Choose Type *</label>
            <div style={{ display:'flex',gap:16 }}>
              {['Reimbursement','Petty Cash'].map(t=>(
                <label key={t} style={{ display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13 }}>
                  <input type="radio" checked={form.expenseType===t} onChange={()=>setForm(f=>({...f,expenseType:t}))} />
                  {t}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginBottom:12 }}><label style={lS}>Title *</label><input style={iS} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} required placeholder="Enter proof title" /></div>
          <div style={{ marginBottom:12 }}><label style={lS}>Date</label><input type="date" style={iS} value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} /></div>
          <div style={{ marginBottom:12 }}><label style={lS}>Description</label><textarea style={{...iS,minHeight:70,resize:'vertical'}} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></div>
          <div style={{ border:'2px dashed #E5E7EB',borderRadius:8,padding:'16px',textAlign:'center',cursor:'pointer',marginBottom:12,fontSize:12,color:'#9CA3AF' }} onClick={()=>fileRef.current?.click()}>
            ☁ Click here to upload or drag files here
            <input ref={fileRef} type="file" style={{ display:'none' }} />
          </div>
          <label style={{ display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',marginBottom:16 }}>
            <input type="checkbox" />
            Split Amount
          </label>
          <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding:'7px 18px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,cursor:'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding:'7px 18px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
              {saving?'Adding…':'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Add Payment Request Modal ─────────────────────────────────────────────────
function AddRequestModal({ clientId, projectId, onClose, onSaved }) {
  const [form, setForm] = useState({ amount:'', title:'', date:new Date().toISOString().split('T')[0], description:'', ...(projectId ? { project: projectId } : {}) });
  const [saving, setSaving] = useState(false);
  const iS = { width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',background:'#F9FAFB',fontFamily:'inherit' };
  const lS = { display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/clients/${clientId}/financials/payment-requests`, form);
      onSaved(); onClose();
    } catch(err) { alert(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}>
      <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:480,boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid #E5E7EB' }}>
          <h3 style={{ margin:0,fontSize:15,fontWeight:700 }}>New Payment Request</h3>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>
        <form onSubmit={submit} style={{ padding:'16px 20px' }}>
          <label style={{ display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',marginBottom:12 }}>
            <input type="checkbox" />Include Milestones
          </label>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div><label style={lS}>Amount *</label><input style={iS} type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} required placeholder="Enter amount" /></div>
            <div><label style={lS}>Date</label><input type="date" style={iS} value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} /></div>
          </div>
          <div style={{ marginBottom:12 }}><label style={lS}>Title *</label><input style={iS} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} required placeholder="Enter title" /></div>
          <div style={{ marginBottom:16 }}><label style={lS}>Description</label><textarea style={{...iS,minHeight:70,resize:'vertical'}} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></div>
          <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding:'7px 18px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,cursor:'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding:'7px 18px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
              {saving?'Adding…':'Add Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Add Payment (Transaction) Modal ───────────────────────────────────────────
function AddPaymentModal({ clientId, projectId, vendors, onClose, onSaved }) {
  const [form, setForm] = useState({ amount:'', date:new Date().toISOString().split('T')[0], referenceNumber:'', channel:'', transactionType:'income', vendor:'', remarks:'', ...(projectId ? { project: projectId } : {}) });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const iS = { width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',background:'#F9FAFB',fontFamily:'inherit' };
  const lS = { display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/clients/${clientId}/financials/transactions`, form);
      onSaved(); onClose();
    } catch(err) { alert(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}>
      <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:460,boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid #E5E7EB' }}>
          <h3 style={{ margin:0,fontSize:15,fontWeight:700 }}>Add Payment</h3>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>
        <form onSubmit={submit} style={{ padding:'16px 20px' }}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div><label style={lS}>Amount *</label><input style={iS} type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} required /></div>
            <div><label style={lS}>Date *</label><input type="date" style={iS} value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} required /></div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div><label style={lS}>Reference Number</label><input style={iS} value={form.referenceNumber} onChange={e=>setForm(f=>({...f,referenceNumber:e.target.value}))} /></div>
            <div><label style={lS}>Type</label>
              <div style={{ display:'flex',gap:6 }}>
                <select style={iS} value={form.transactionType} onChange={e=>setForm(f=>({...f,transactionType:e.target.value}))}>
                  <option value="income">Income (Received)</option>
                  <option value="expense">Expense (Paid)</option>
                </select>
                <button type="button" style={{ width:30,height:36,border:'none',background:'#976793',borderRadius:6,color:'#fff',cursor:'pointer',fontWeight:700 }}>+</button>
              </div>
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={lS}>Channel</label>
            <div style={{ display:'flex',gap:6 }}>
              <select style={iS} value={form.channel} onChange={e=>setForm(f=>({...f,channel:e.target.value}))}>
                <option value="">Select channel…</option>
                {['Cash','Bank Transfer','UPI','Cheque','Card','Other'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              <button type="button" style={{ width:30,height:36,border:'none',background:'#976793',borderRadius:6,color:'#fff',cursor:'pointer',fontWeight:700 }}>+</button>
            </div>
          </div>
          <div style={{ marginBottom:12 }}><label style={lS}>Remarks</label><textarea style={{...iS,minHeight:60,resize:'vertical'}} value={form.remarks} onChange={e=>setForm(f=>({...f,remarks:e.target.value}))} /></div>
          <div style={{ marginBottom:16 }}>
            <label style={lS}>Receipt</label>
            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
              <button type="button" onClick={()=>fileRef.current?.click()} style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',background:'#976793',color:'#fff',border:'none',borderRadius:6,fontSize:12,cursor:'pointer' }}>
                ☁ Upload
              </button>
              <span style={{ fontSize:12,color:'#9CA3AF' }}>No file selected</span>
              <input ref={fileRef} type="file" style={{ display:'none' }} />
            </div>
          </div>
          <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding:'7px 18px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,cursor:'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding:'7px 18px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
              {saving?'Saving…':'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Financials Tab ───────────────────────────────────────────────────────
export default function FinancialsTab({ clientId, projectId }) {
  const [summary, setSummary]         = useState(null);
  const [activeTab, setActiveTab]     = useState('requests');
  const [requests, setRequests]       = useState([]);
  const [expenses, setExpenses]       = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [vendors, setVendors]         = useState([]);
  const [latestQuote, setLatestQuote] = useState(null);
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showExpenseModal, setShowExpenseModal]   = useState(false);
  const [showRequestModal, setShowRequestModal]   = useState(false);
  const [showPaymentModal, setShowPaymentModal]   = useState(false);
  const [reqSubTab, setReqSubTab]     = useState('all');
  const [expSubTab, setExpSubTab]     = useState('all');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [listSearch, setListSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = Date.now();
      const requestParams = projectId ? { limit: 100, project: projectId, fresh } : { limit: 100, fresh };
      const summaryParams = projectId ? { project: projectId, fresh } : { fresh };
      const scopedListParams = projectId ? { limit: 100, project: projectId, fresh } : { limit: 100, fresh };
      const [sR, rR, eR, tR, vR, qR, receiptR] = await Promise.all([
        api.get(`/clients/${clientId}/financials/summary`, { params: summaryParams }),
        api.get(`/clients/${clientId}/financials/payment-requests`,{params:requestParams}),
        api.get(`/clients/${clientId}/financials/expenses`,{params:scopedListParams}),
        api.get(`/clients/${clientId}/financials/transactions`,{params:scopedListParams}),
        api.get('/vendors',{params:{limit:200, fresh}}),
        projectId ? api.get('/quotes', { params: { client: clientId, project: projectId, limit: 100, page: 1, fresh } }) : Promise.resolve(null),
        projectId ? api.get(`/clients/${clientId}/projects/${projectId}/payment-receipts`, { params: { fresh } }) : Promise.resolve(null),
      ]);
      setSummary(sR.data.data);
      setRequests(rR.data.data.items||[]);
      setExpenses(eR.data.data.items||[]);
      setTransactions(tR.data.data.items||[]);
      setVendors(vR.data.data.vendors||[]);
      const quoteList = qR?.data?.data?.quotes || [];
      const sortedQuotes = quoteList
        .filter((q) => q && q.status !== 'rejected' && Number(q.total) > 0)
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
      setLatestQuote(sortedQuotes[0] || null);
      setPaymentReceipts(receiptR?.data?.data?.items || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [clientId, projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setListSearch(''); }, [activeTab]);

  const filteredRequestsList = useMemo(() => {
    const list = reqSubTab === 'all' ? requests : requests.filter((r) => r.status === reqSubTab);
    if (!listSearch.trim()) return list;
    return list.filter((r) =>
      textHaystackIncludes([r.title, r.requestedBy?.name, r.status, r.date, String(r.amount || '')], listSearch)
    );
  }, [requests, reqSubTab, listSearch]);

  const filteredExpensesList = useMemo(() => {
    const list = expSubTab === 'all' ? expenses : expenses.filter((e) => e.status === expSubTab);
    if (!listSearch.trim()) return list;
    return list.filter((e) =>
      textHaystackIncludes([e.title, e.createdBy?.name, e.status, e.date, String(e.amount || '')], listSearch)
    );
  }, [expenses, expSubTab, listSearch]);

  const filteredTransactionsList = useMemo(() => {
    if (!listSearch.trim()) return transactions;
    return transactions.filter((t) =>
      textHaystackIncludes(
        [t.createdBy?.name, t.vendor?.displayName, t.transactionType, t.channel, String(t.amount || ''), t.remarks],
        listSearch
      )
    );
  }, [transactions, listSearch]);

  const rawSummary = summary;
  const receiptsByMilestonePct = useMemo(() => {
    return paymentReceipts.reduce((acc, rec) => {
      if (rec?.milestonePct == null) return acc;
      const key = String(rec.milestonePct);
      const current = acc[key];
      if (
        !current ||
        (!!rec.locked && !current.locked) ||
        (!!rec.locked === !!current.locked && new Date(rec.receivedAt || 0) > new Date(current.receivedAt || 0))
      ) {
        acc[key] = rec;
      }
      return acc;
    }, {});
  }, [paymentReceipts]);
  const financialMilestones = useMemo(() => {
    if (!projectId || !latestQuote) return [];
    const amounts = splitTotalIntoMilestones(latestQuote.total);
    return calculateMilestoneRows(
      MILESTONE_DISPLAY_ORDER.map((pct, idx) => ({
        _id: `${latestQuote._id || 'active'}-${pct}`,
        title: MILESTONE_TITLES[pct] || `Payment reminder - ${pct}%`,
        milestonePct: pct,
        amount: amounts[idx],
        status: 'pending',
      })).filter((r) => r.amount > 0),
      receiptsByMilestonePct
    );
  }, [projectId, latestQuote, receiptsByMilestonePct]);
  const milestoneTotalAmount = financialMilestones.reduce((sum, row) => sum + row.milestoneAmount, 0);
  const milestoneReceivedAmount = financialMilestones.reduce((sum, row) => sum + row.received, 0);
  const milestonePendingAmount = financialMilestones.length ? financialMilestones[financialMilestones.length - 1].pending : 0;
  const financialMilestoneByPct = useMemo(() => {
    return financialMilestones.reduce((acc, row) => {
      acc[String(row.milestonePct)] = row;
      return acc;
    }, {});
  }, [financialMilestones]);
  const displaySummary = projectId && latestQuote
    ? (() => {
        const vendorInvoice = rawSummary?.vendorInvoice || 0;
        const profitLoss = milestoneTotalAmount - vendorInvoice;
        return {
          ...(rawSummary || {}),
          quotesApproved: { withoutGst: Number(latestQuote.subTotal) || 0, gst: Number(latestQuote.gstTotal) || 0, total: milestoneTotalAmount },
          clientInvoice: milestoneTotalAmount,
          vendorInvoice,
          profitLoss,
          margin: {
            value: milestoneTotalAmount > 0 ? ((profitLoss / milestoneTotalAmount) * 100).toFixed(1) : 0,
            totalRs: profitLoss,
          },
          paymentReceived: milestoneReceivedAmount,
          paymentDone: rawSummary?.paymentDone || 0,
          cashflow: milestoneReceivedAmount - (rawSummary?.paymentDone || 0),
        };
      })()
    : rawSummary;
  const s = displaySummary;
  const revenueTotal = s?.clientInvoice || 0;
  const expenseTotal = s?.vendorInvoice || 0;
  const profitLossTotal = s?.profitLoss || 0;

  const procurementByVendor = s?.hardwarePurchases?.byVendor || [];
  const labourByVendor = s?.labourCharges?.byVendor || [];
  const procurementTotal = s?.hardwarePurchases?.total || procurementByVendor.reduce((a, row) => a + (row.amount || 0), 0);
  const workOrderLabourTotal = s?.labourCharges?.workOrderTotal || labourByVendor.reduce((a, row) => a + (row.amount || 0), 0);
  const otherOrderCharges = s?.labourCharges?.otherOrderCharges || 0;
  const procurementRows = procurementByVendor.length
    ? procurementByVendor
    : procurementTotal > 0
      ? [{ vendorId: 'approved-procurement-total', vendorName: 'Approved Procurement Total', amount: procurementTotal }]
      : [];
  const workOrderRows = labourByVendor.length
    ? labourByVendor
    : workOrderLabourTotal > 0
      ? [{ vendorId: 'approved-work-order-total', vendorName: 'Approved Work Order Total', amount: workOrderLabourTotal }]
      : [];

  // Excel export for transactions
  const exportTransactions = () => {
    const rows = [
      ['Created On','Date','Created By','Vendor','Transaction Type','Channel','Amount','Remarks'],
      ...transactions.map(t=>[
        t.createdAt?new Date(t.createdAt).toLocaleDateString('en-IN'):'',
        t.date?new Date(t.date).toLocaleDateString('en-IN'):'',
        t.createdBy?.name||'',
        t.vendor?.displayName||'',
        t.transactionType,
        t.channel||'',
        t.amount||0,
        t.remarks||'',
      ])
    ];
    const csv = rows.map(r=>r.map(v=>`"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const a = document.createElement('a'); a.href=URL.createObjectURL(blob); a.download='transactions.csv'; a.click();
  };

  const thS = { padding:'7px 10px',fontSize:11,fontWeight:600,color:'#6B7280',borderBottom:'1px solid #E5E7EB',textAlign:'left',whiteSpace:'nowrap',background:'#F9FAFB' };
  const tdS = { padding:'8px 10px',fontSize:12,color:'#374151',borderBottom:'1px solid #F3F4F6',verticalAlign:'middle' };
  const boxS = { background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'16px 20px',flex:1 };
  const boxTitleS = { margin:'0 0 12px',fontSize:14,fontWeight:700,color:'#1F2937' };

  return (
    <div style={{ fontFamily:'inherit' }}>
      {/* ── 3 Summary Boxes ── */}
      <div style={{ display:'flex',gap:12,marginBottom:20 }}>
        {/* Box 1: Quotes / Hardware / Labour / Margin */}
        <div style={boxS}>
          <h3 style={boxTitleS}>Expense Summary</h3>
          <div style={{ marginBottom:14 }}>
            <div style={{ display:'flex',justifyContent:'space-between',fontSize:12,fontWeight:700,color:'#B45309',marginBottom:6 }}>
              <span>Procurement</span>
              <span>{fmt(procurementTotal)}</span>
            </div>
            {procurementRows.length === 0 ? (
              <div style={{ fontSize:11,color:'#9CA3AF',paddingLeft:12,marginBottom:4 }}>No approved procurement yet</div>
            ) : procurementRows.map((row) => (
              <div key={row.vendorId || row.vendorName} style={{ display:'flex',justifyContent:'space-between',gap:10,fontSize:11,color:'#6B7280',marginBottom:4,paddingLeft:12 }}>
                <span style={{ minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{row.vendorName || 'Unassigned Vendor'}</span>
                <span style={{ fontWeight:600,whiteSpace:'nowrap' }}>{fmt(row.amount)}</span>
              </div>
            ))}
          </div>
          <div style={{ marginBottom:12,borderTop:'1px dashed #E5E7EB',paddingTop:10 }}>
            <div style={{ display:'flex',justifyContent:'space-between',fontSize:12,fontWeight:700,color:'#B45309',marginBottom:6 }}>
              <span>Work Orders</span>
              <span>{fmt(workOrderLabourTotal)}</span>
            </div>
            {workOrderRows.length === 0 ? (
              <div style={{ fontSize:11,color:'#9CA3AF',paddingLeft:12,marginBottom:4 }}>No approved work order labour yet</div>
            ) : workOrderRows.map((row) => (
              <div key={row.vendorId || row.vendorName} style={{ display:'flex',justifyContent:'space-between',gap:10,fontSize:11,color:'#6B7280',marginBottom:4,paddingLeft:12 }}>
                <span style={{ minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{row.vendorName || 'Unassigned Vendor'}</span>
                <span style={{ fontWeight:600,whiteSpace:'nowrap' }}>{fmt(row.amount)}</span>
              </div>
            ))}
            {otherOrderCharges !== 0 && (
              <div style={{ display:'flex',justifyContent:'space-between',gap:10,fontSize:11,color:'#6B7280',marginBottom:4,paddingLeft:12 }}>
                <span>Other Order Charges</span>
                <span style={{ fontWeight:600 }}>{fmt(otherOrderCharges)}</span>
              </div>
            )}
          </div>
          <div style={{ borderTop:'1px solid #F3F4F6',paddingTop:10 }}>
            <div style={{ display:'flex',justifyContent:'space-between',fontWeight:700,fontSize:13,color:'#1F2937' }}>
              <span>Total Expenses</span>
              <span>{fmt(expenseTotal)}</span>
            </div>
          </div>
        </div>

        {/* Box 2: Invoices / Profit/Loss */}
        <div style={boxS}>
          <h3 style={boxTitleS}>Revenue & Profit/Loss</h3>
          <SummaryStat label="Revenue" value={revenueTotal} color='#10B981' showInfo withoutGst={s?.quotesApproved?.withoutGst||0} gst={s?.quotesApproved?.gst||0} total={revenueTotal} />
          <SummaryStat label="Expenses" value={expenseTotal} color='#B45309' showInfo withoutGst={(s?.hardwarePurchases?.withoutGst||0) + (s?.labourCharges?.total||0)} gst={s?.hardwarePurchases?.gst||0} total={expenseTotal} />
          <div>
            <div style={{ fontSize:12,color:'#6B7280',marginBottom:4,display:'flex',alignItems:'center',gap:6 }}>
              <span style={{ color: profitLossTotal>=0?'#10B981':'#EF4444' }}>▲</span>
              Profit/Loss
            </div>
            <div style={{ fontSize:18,fontWeight:700,color:profitLossTotal>=0?'#10B981':'#EF4444' }}>
              {fmt(profitLossTotal)}
            </div>
          </div>
        </div>

        {/* Box 3: Payment Received / Done / Cashflow */}
        <div style={boxS}>
          <h3 style={boxTitleS}>Cashflow</h3>
          <div style={{ marginBottom:12 }}>
            <div style={{ display:'flex',alignItems:'center',gap:4,fontSize:12,color:'#6B7280',marginBottom:2 }}>
              <span>↑</span> Payment Received
            </div>
            <div style={{ fontSize:16,fontWeight:700,color:'#1F2937' }}>{fmt(s?.paymentReceived||0)}</div>
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ display:'flex',alignItems:'center',gap:4,fontSize:12,color:'#6B7280',marginBottom:2 }}>
              <span>✓</span> Payment Done
            </div>
            <div style={{ fontSize:16,fontWeight:700,color:'#1F2937' }}>{fmt(s?.paymentDone||0)}</div>
          </div>
          <div>
            <div style={{ display:'flex',alignItems:'center',gap:4,fontSize:12,color:'#6B7280',marginBottom:2 }}>
              <span>⇄</span> Net Cashflow
            </div>
            <div style={{ fontSize:18,fontWeight:700,color:(s?.cashflow||0)>=0?'#10B981':'#EF4444' }}>
              {fmt(s?.cashflow||0)}
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab Bar ── */}
      <div style={{ display:'flex',borderBottom:'1px solid #E5E7EB',marginBottom:16 }}>
        {[['requests','All Payment Requests'],['expenses','All Expenses'],['transactions','Transactions']].map(([key,label])=>(
          <button key={key} onClick={()=>setActiveTab(key)}
            style={{ padding:'8px 14px',fontSize:12,fontWeight:activeTab===key?600:400,color:activeTab===key?'#976793':'#6B7280',background:'transparent',border:'none',borderBottom:activeTab===key?'2px solid #976793':'2px solid transparent',cursor:'pointer',marginBottom:-1,display:'flex',alignItems:'center',gap:5 }}>
            {activeTab===key && <span style={{ fontSize:11 }}>✓</span>}
            {label}
          </button>
        ))}
      </div>

      <TableSearchInput
        value={listSearch}
        onChange={(e) => setListSearch(e.target.value)}
        placeholder={
          activeTab === 'requests'
            ? 'Search payment requests…'
            : activeTab === 'expenses'
              ? 'Search expenses…'
              : 'Search transactions…'
        }
        style={{ maxWidth: '100%', marginBottom: 14 }}
      />

      {/* ── Dashboard + Table ── */}
      <div>
        {/* Stat cards */}
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14 }}>
          <h3 style={{ fontSize:15,fontWeight:700,color:'#1F2937',margin:0 }}>Dashboard</h3>
          <div style={{ display:'flex',gap:8 }}>
            <button onClick={()=>setShowExpenseModal(true)}
              style={{ padding:'7px 14px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:12,fontWeight:500,cursor:'pointer',color:'#374151' }}>
              Add Expense
            </button>
            <button onClick={()=>setShowRequestModal(true)}
              style={{ padding:'7px 14px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}>
              Add Request
            </button>
          </div>
        </div>

        {/* Summary stat cards */}
        {activeTab === 'requests' && (
          <>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16 }}>
              {[
                ['Total Requested Amount', projectId && latestQuote ? milestoneTotalAmount : requests.reduce((a,r)=>a+(r.amount||0),0)],
                ['Total Approved Amount',  requests.filter(r=>r.status==='approved').reduce((a,r)=>a+(r.amount||0),0)],
                ['Total Pending for Approval', projectId && latestQuote ? milestonePendingAmount : requests.filter(r=>r.status==='pending').reduce((a,r)=>a+(r.amount||0),0)],
                ['Total Paid Amount', projectId && latestQuote ? milestoneReceivedAmount : requests.filter(r=>r.status==='paid').reduce((a,r)=>a+(r.amount||0),0)],
              ].map(([label,val])=>(
                <div key={label} style={{ background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 16px' }}>
                  <div style={{ fontSize:11,color:'#6B7280',marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:18,fontWeight:700,color:'#1F2937' }}>₹ {Number(val||0).toLocaleString('en-IN')}</div>
                </div>
              ))}
            </div>
            {/* Sub-tabs */}
            <div style={{ display:'flex',gap:6,marginBottom:12,flexWrap:'wrap' }}>
              {[['all','All Requests'],['pending','Pending For Approvals'],['approved','Approved'],['partially_paid','Partially Paid'],['paid','Paid'],['rejected','Rejected']].map(([k,l])=>(
                <button key={k} onClick={()=>setReqSubTab(k)}
                  style={{ padding:'4px 12px',borderRadius:20,border:'1px solid #E5E7EB',fontSize:11,fontWeight:500,cursor:'pointer',
                    background:reqSubTab===k?'#976793':'#fff', color:reqSubTab===k?'#fff':'#374151' }}>
                  {l} ({k==='all'?requests.length:requests.filter(r=>r.status===k).length})
                </button>
              ))}
              <button style={{ marginLeft:'auto',display:'flex',alignItems:'center',gap:4,padding:'4px 10px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:11,cursor:'pointer' }}>
                📊 Export Excel
              </button>
            </div>
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={thS}><input type="checkbox" /></th>
                {['Title','Project Name','Requested By','Requested Amount','Requested date','Total Wallet Balance','Status','Actions'].map(h=>(
                  <th key={h} style={thS}>{h}<br/><input style={{ marginTop:3,width:'90%',padding:'2px 5px',border:'1px solid #E5E7EB',borderRadius:4,fontSize:10,outline:'none' }} /></th>
                ))}
              </tr></thead>
              <tbody>
                {filteredRequestsList.length===0 ? (
                  <tr><td colSpan={9} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>{listSearch.trim() ? 'No rows match your search' : 'No data available'}</td></tr>
                ) : filteredRequestsList.map(r=>{
                  const milestoneRow = projectId && r.origin === 'quote_milestone' ? financialMilestoneByPct[String(r.milestonePct)] : null;
                  const shownAmount = milestoneRow ? milestoneRow.netPayable : r.amount;
                  const shownWallet = milestoneRow
                    ? milestoneRow.forwardsToNext
                      ? `Forwarded (${fmt(milestoneRow.pending)})`
                      : fmt(milestoneRow.displayedPending)
                    : '—';
                  return (
                    <tr key={r._id}>
                      <td style={tdS}><input type="checkbox" /></td>
                      <td style={tdS}>{r.title}</td>
                      <td style={tdS}>{projectId ? (latestQuote?.title || 'Project') : '—'}</td>
                      <td style={tdS}>{r.requestedBy?.name||'—'}</td>
                      <td style={tdS}>{fmt(shownAmount)}</td>
                      <td style={tdS}>{r.date?new Date(r.date).toLocaleDateString('en-IN'):'—'}</td>
                      <td style={tdS}>{shownWallet}</td>
                      <td style={tdS}><span style={{ background:'#FEF3C7',color:'#D97706',padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:600,textTransform:'capitalize' }}>{r.status}</span></td>
                      <td style={tdS}>—</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {activeTab === 'expenses' && (
          <>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16 }}>
              {[
                ['Total Expense', expenses.reduce((a,e)=>a+(e.amount||0),0)],
                ['Approved Expenses', expenses.filter(e=>e.status==='approved').reduce((a,e)=>a+(e.amount||0),0)],
                ['Pending for Approval', expenses.filter(e=>e.status==='pending').reduce((a,e)=>a+(e.amount||0),0)],
                ["Tatsavi Spaces's Wallet", 215000],
              ].map(([label,val])=>(
                <div key={label} style={{ background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 16px' }}>
                  <div style={{ fontSize:11,color:'#6B7280',marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:18,fontWeight:700,color:'#1F2937' }}>₹ {Number(val||0).toLocaleString('en-IN')}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex',gap:6,marginBottom:12,flexWrap:'wrap' }}>
              {[['all','All Expenses'],['pending','Pending'],['approved','Approved'],['rejected','Rejected']].map(([k,l])=>(
                <button key={k} onClick={()=>setExpSubTab(k)}
                  style={{ padding:'4px 12px',borderRadius:20,border:'1px solid #E5E7EB',fontSize:11,fontWeight:500,cursor:'pointer',
                    background:expSubTab===k?'#976793':'#fff', color:expSubTab===k?'#fff':'#374151' }}>
                  {l} ({k==='all'?expenses.length:expenses.filter(e=>e.status===k).length})
                </button>
              ))}
              <div style={{ display:'flex',gap:4,marginLeft:'auto' }}>
                <button style={{ display:'flex',alignItems:'center',gap:4,padding:'4px 10px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:11,cursor:'pointer',color:'#374151' }}>
                  📊 Export Excel
                </button>
              </div>
            </div>
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={thS}><input type="checkbox" /></th>
                {['Title','Project Name','Total Amount','Added By','Added on','Status','Proof','Actions'].map(h=>(
                  <th key={h} style={thS}>{h}<br/><div style={{ marginTop:2 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg></div></th>
                ))}
              </tr></thead>
              <tbody>
                {filteredExpensesList.length===0 ? (
                  <tr><td colSpan={9} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>{listSearch.trim() ? 'No rows match your search' : 'No data available'}</td></tr>
                ) : filteredExpensesList.map(e=>(
                  <tr key={e._id}>
                    <td style={tdS}><input type="checkbox" /></td>
                    <td style={tdS}>{e.title}</td>
                    <td style={tdS}>—</td>
                    <td style={tdS}>{fmt(e.amount)}</td>
                    <td style={tdS}>{e.createdBy?.name||'—'}</td>
                    <td style={tdS}>{e.date?new Date(e.date).toLocaleDateString('en-IN'):'—'}</td>
                    <td style={tdS}><span style={{ background:'#FEF3C7',color:'#D97706',padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:600,textTransform:'capitalize' }}>{e.status}</span></td>
                    <td style={tdS}>—</td>
                    <td style={tdS}>—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {activeTab === 'transactions' && (
          <>
            <div style={{ display:'flex',justifyContent:'flex-end',gap:6,marginBottom:12 }}>
              <button onClick={()=>setShowPaymentModal(true)}
                style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}>
                + Payment
              </button>
              <div style={{ position:'relative' }}>
                <button onClick={()=>setShowMoreMenu(m=>!m)} style={{ width:30,height:30,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF' }}>⋮</button>
                {showMoreMenu && (
                  <div style={{ position:'absolute',right:0,top:34,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',zIndex:100,overflow:'hidden',minWidth:130 }}>
                    <button onClick={()=>{ exportTransactions(); setShowMoreMenu(false); }}
                      style={{ display:'block',width:'100%',textAlign:'left',padding:'8px 14px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color:'#374151' }}
                      onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      📊 Export
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%',borderCollapse:'collapse',minWidth:1200 }}>
                <thead><tr>
                  {['Created On','Date','Created By','Vendor','Transaction','Channel','Amount','Receipt','User','Order','Invoice','Tasks Created','Details','Comments','Remarks','Actions'].map(h=>(
                    <th key={h} style={thS}>{h}
                      {['Vendor','Channel'].includes(h) && <><br/><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg></>}
                    </th>
                  ))}
                </tr></thead>
                <tbody>
                  {filteredTransactionsList.length===0 ? (
                    <tr><td colSpan={16} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>{listSearch.trim() ? 'No rows match your search' : 'No data available'}</td></tr>
                  ) : filteredTransactionsList.map(t=>(
                    <tr key={t._id}>
                      <td style={tdS}>{t.createdAt?new Date(t.createdAt).toLocaleDateString('en-IN'):'—'}</td>
                      <td style={tdS}>{t.date?new Date(t.date).toLocaleDateString('en-IN'):'—'}</td>
                      <td style={tdS}>{t.createdBy?.name||'—'}</td>
                      <td style={tdS}>{t.vendor?.displayName||'—'}</td>
                      <td style={tdS}><span style={{ background:t.transactionType==='income'?'#D1FAE5':'#FEE2E2',color:t.transactionType==='income'?'#059669':'#EF4444',padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:600 }}>{t.transactionType}</span></td>
                      <td style={tdS}>{t.channel||'—'}</td>
                      <td style={{ ...tdS,fontWeight:600 }}>{fmt(t.amount)}</td>
                      <td style={tdS}>—</td>
                      <td style={tdS}>—</td>
                      <td style={tdS}>—</td>
                      <td style={tdS}>—</td>
                      <td style={tdS}>—</td>
                      <td style={tdS}>—</td>
                      <td style={tdS}>—</td>
                      <td style={tdS}>{t.remarks||'—'}</td>
                      <td style={tdS}>—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize:12,color:'#9CA3AF',padding:'8px 0' }}>
              Showing {filteredTransactionsList.length} of {transactions.length} items
            </div>
          </>
        )}

        {/* Pagination */}
        {activeTab !== 'transactions' && (
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 0',fontSize:12,color:'#6B7280',borderTop:'1px solid #E5E7EB',marginTop:8 }}>
            <span>0 Items</span>
            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
              <button style={{ width:24,height:24,border:'1px solid #E5E7EB',borderRadius:4,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF' }}>‹</button>
              <button style={{ width:24,height:24,border:'1px solid #E5E7EB',borderRadius:4,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF' }}>›</button>
              <select style={{ padding:'2px 6px',border:'1px solid #E5E7EB',borderRadius:4,fontSize:11,outline:'none' }}>
                <option>10</option><option>25</option><option>50</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {showExpenseModal && <AddExpenseModal clientId={clientId} projectId={projectId} onClose={()=>setShowExpenseModal(false)} onSaved={load} />}
      {showRequestModal && <AddRequestModal clientId={clientId} projectId={projectId} onClose={()=>setShowRequestModal(false)} onSaved={load} />}
      {showPaymentModal && <AddPaymentModal clientId={clientId} projectId={projectId} vendors={vendors} onClose={()=>setShowPaymentModal(false)} onSaved={load} />}
    </div>
  );
}

